import React from 'react';
import { orderBy } from '@progress/kendo-data-query';
import { ListBoxItemClickEvent } from '@progress/kendo-react-listbox';
import SimpleDragDropItem, {
    SimpleDragDropItemProps,
} from '@components-clean/core/drag-drop-lists-core/drag-drop-item-simple';
import {
    DragDropColumnsState,
    DragDropListItemProps,
    ProcessDragDropListResult,
    Side,
} from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';

export const DRAG_DROP_SELECTED_FIELD = 'selected';
export const DRAG_DROP_FILTER_HEIGHT = 34;

export const parseDragDropItems = (items: DragDropListItemProps[]) =>
    items?.map((item: DragDropListItemProps) => ({
        ...item,
        selected: item.selected ?? false,
        filtered: item.filtered ?? true,
    }));

export const sortDragDropItems = (items: DragDropListItemProps[], sort: 'asc' | 'desc' = 'asc') =>
    orderBy(items, [{ field: 'title', dir: sort }]);

export const DefaultDragDropItem = (props: SimpleDragDropItemProps) => (
    <SimpleDragDropItem {...props} toDisplayColumn />
);

export const updateDragDropClickedItem = (
    state: DragDropColumnsState,
    event: ListBoxItemClickEvent,
    data: Side,
    connectedData: Side,
    idField: Extract<keyof DragDropListItemProps, string>,
) => ({
    ...state,
    [data]: state[data].map((item: DragDropListItemProps) => {
        if (item[idField] === (event.dataItem as DragDropListItemProps)[idField]) {
            item[DRAG_DROP_SELECTED_FIELD] = !item[DRAG_DROP_SELECTED_FIELD];
        } else if (!(event.nativeEvent as KeyboardEvent).ctrlKey) {
            item[DRAG_DROP_SELECTED_FIELD] = false;
        }
        return item;
    }),
    [connectedData]: state[connectedData].map((item: DragDropListItemProps) => {
        item[DRAG_DROP_SELECTED_FIELD] = false;
        return item;
    }),
});

export const highlightDroppedItem = (
    data: ProcessDragDropListResult,
    draggedItem: DragDropListItemProps,
    idField: Extract<keyof DragDropListItemProps, string>,
) => {
    const updateItems = (items: DragDropListItemProps[]) =>
        items.map((item) => ({
            ...item,
            [DRAG_DROP_SELECTED_FIELD]: item[idField] === draggedItem[idField],
        }));

    return {
        listBoxOneData: updateItems(data.listBoxOneData),
        listBoxTwoData: updateItems(data.listBoxTwoData),
    };
};

export const highlightDroppedItems = (
    data: ProcessDragDropListResult,
    draggedItems: DragDropListItemProps[],
    idField: Extract<keyof DragDropListItemProps, string>,
) => {
    const updateItems = (items: DragDropListItemProps[]) =>
        items.map((item) => ({
            ...item,
            [DRAG_DROP_SELECTED_FIELD]: draggedItems.some((i) => i[idField] === item[idField]),
        }));

    return {
        listBoxOneData: updateItems(data.listBoxOneData),
        listBoxTwoData: updateItems(data.listBoxTwoData),
    };
};

const orderDragDropFilteredArray = (
    filteredArray: DragDropListItemProps[],
    previousArray: DragDropListItemProps[],
    idField: Extract<keyof DragDropListItemProps, string>,
) => {
    // Create a map to store the index of each item in previousArray
    const indexMap = new Map(previousArray.map((item, index) => [item[idField], index]));
    // Sort filteredArray based on the index of each item in previousArray
    filteredArray.sort((a, b) => indexMap.get(a[idField])! - indexMap.get(b[idField])!);

    return filteredArray;
};

export const filterDragDropColumn = (
    currentListState: DragDropListItemProps[],
    searchString: string,
    idField: Extract<keyof DragDropListItemProps, string>,
    filterItem?: (dataItem: DragDropListItemProps, filter: string) => boolean | undefined,
) => {
    if (searchString === '') return currentListState.map((item) => ({ ...item, filtered: true }));

    const filterText = searchString.toLocaleLowerCase();
    const filteredItems = currentListState.map((item) => {
        let filtered;
        if (filterItem) filtered = filterItem(item, filterText);
        else {
            const fullPanelName = item.title;
            filtered = filterText === '' || fullPanelName?.toString().toLocaleLowerCase().includes(filterText);
        }

        return {
            ...item,
            selected: filtered ? item.selected : false,
            filtered,
        };
    });

    return orderDragDropFilteredArray(
        filteredItems,
        currentListState.filter((item) => item.filtered),
        idField,
    );
};
